import {getGeometry, diameter, centroid} from "effects/midi/mappable/lang/geometry.js";
import Colorable from "engine/Colorable.js";
import {getCoordinates} from './getCoordinates.js';
import {spline} from './spline.js';
import {getTanBlob} from './tanBlob.js';
import {makeHull} from './convexHull.js';
import ParameterContext from "effects/ParameterContext.js";
import SubMacro from 'effects/SubMacro.js';
import {AbstractAudioParamParameter} from 'parameters/AbstractAudioParamParameter.js';
import Tone from 'tone';
import {WIDTH, HEIGHT, PADDING} from 'components/effects/plane/constants.js';
import {uuid} from 'uuidv4';
import {setPlaneMacros} from 'actions/index.js';

export default superclass => class extends superclass  {
    macros = [];

    deleteMacro(macro) {
        this.macros =  this.macros.filter(x => x !== macro);
        this.updateMacroRedux();
        macro.dispose();
    }

    createMacro(parameters) {
        let name = "Macro " + (this.macros.length + 1);
        let macro = new Macro(name, parameters, this.macros.length + 1, this);
        parameters.forEach(
            parameter => parameter.planeMacro = macro);
        this.addMacro(macro);
   }

    freeMacro(macro) {
        macro.free();
        this.deleteMacro(macro);
    }

    updateMacroRedux() {
        this.track.sequencerEngine.dispatch(
            setPlaneMacros(
                this.id,
                this.macros));
    }

    addMacro(macro) {
        this.macros = [... this.macros, macro];
        macro.number = this.macros.length;
        // this.newOutlet("Macro " + this.macros.length);
        this.updateMacroRedux();
    }

    sendMacroMessages() {
        let dateA = new Date().getTime();
        let map = [];
        for (let i=1; i <= this.macros.length; i++) {
            if (this.isOutletConnected("macros")) {
                let macro = this.macros[i-1];
                let coords = macro.getAllCoordinates(true);
                map.push(coords);
            }
        }
        let dateB = new Date().getTime();
        this.sendMessage("macros", map);
    }

    getMacrosJson() {
        return this.macros.map(x => x.getJson());
    }

    fromMacrosJson(json) {
        if (!json) {
            return;
        }
        for (let m of json) {
            let macro = new Macro("macro", [], 0, this);
            macro.fromJson(m);
            this.addMacro(macro);
        }
    }
}

/**
 * A macro in a Plane Effect, is simply a way of reducing
 * many parameters (on the 2D plane) into 1 point.
 * Geometrically, it is the centroid of all the points
 * Thus when the (plane group) algorithm alters the underlying parameters
 * the centroid might move
 */
class Macro extends Colorable(class {}){
    constructor(name, parameters, number, effect) {
        super();
        this.id = uuid();
        this.name = name;
        this.number = number;
        this.parameters = parameters;
        this.effect = effect;
        this.signal = new Tone.Signal(0);
        this.signalParameter = new AbstractAudioParamParameter(
            'macro',
            this.signal, () => {},
            {
                min: 0,
                defaultValue: 0,
                max: 1,
                useInstrumentModulators: true
            });
        this.signalParameter.setContext(
            new ParameterContext().forTrack(this.effect.track));
        this.subMacros = {};
        this.initColor();
    }

    dispose() {
        this.lastHull = undefined;
        this.lastCentroid  = undefined;
        let lfos = this.effect.track.sequencerEngine.lfos.map(x => x.lfo);
        for (let lfoNumber in this.subMacros) {
            let lfo = lfos.find(l => l.number === parseInt(lfoNumber));
            this.disconnectFromLFO(lfo);
        }
        this.parameters.forEach(
            p => p.planeMacro = undefined);
        this.signalParameter.dispose();
        this.signal.dispose();
        this.signalParameter = undefined;
        this.subMacros = undefined;
        this._disposed = true;
    }

    disposeSubMacro(m) {
        let parameter = m.parameter;
        this.signal.disconnect(m.scaler);
        m.dispose();
        parameter.set(parameter.get(true), true, true);
    }

    disconnectFromLFO(lfo) {
        let subMacros = this.subMacros[lfo.number];
        subMacros.forEach(m => {
            this.disposeSubMacro(m);
        });
        delete subMacros[lfo.number];
        this.signalParameter.disconnectFromLFO(lfo);
    }

    connectToLFO(lfo, connectLFO=true) {
        // first connect signal to each parameter
        let parameters = this.parameters
            .filter(
                // ensure its not already connected
                p => !this.subMacros[lfo.number] ||
                    !this.subMacros[lfo.number].some(m => m.parameter === p))
            .filter(
                // we dont want to connect an lfo to itself
                // as it will cause feedback problems and 'essplode
                p => p.context &&
                    (!p.context.lfoTrack ||
                     p.context.lfoTrack.lfo !== lfo))
            .filter(
                // this tends to be a problem
                p => p.name !== "LFO Max" &&
                    p.name !== "LFO Phase")
            .filter(
                p => p.audioParam || p.audioParams);
        let alreadyConnected = this.subMacros[lfo.number] !== undefined;
        let subMacros = this.subMacros[lfo.number] || [];
        this.subMacros[lfo.number] = [
            ... subMacros,
            ...parameters.map(
                p => {
                    let subMacro = new SubMacro(
                        p, p.min, p.max);
                    subMacro.withSignal(this.signal);
                    
                    p.set(p.get(true), true, true);
                    return subMacro;
                })
        ];

        // need to make sure we arent already connected
        if (connectLFO && !this.lfos.includes(lfo)) {
            this.signalParameter.connectToLFO(lfo);
        }
    }

    fromJson(json, isCopy) {
        this.name = json.name;
        this.id = json.id;
        this.number = json.number;
        this.parameterIds = json.parameters;
        this.signalParameterJson = json.signalParameter;
        this.isCopy = isCopy;
        if (json.color) {
            this.color = json.color;
        } else {
            this.initColor();
        }
   }

    loadParameters(parameters, newMappings) {
        let parameterIds = this.parameterIds.map(
            id => newMappings[id] ? newMappings[id].id : id);
        for (let parameter of parameters) {
            if (parameterIds.includes(parameter.id) &&
                !this.parameters.includes(parameter)) {
                this.parameters.push(parameter);
                parameter.planeMacro = this;
            }
        }

        if (this.signalParameterJson) {
            // this essentially loads the lfos/modulation depths
            // for the signalParameter
            if (!this._loadedSignalParameter) {
                this.signalParameter.fromJson(
                    this.signalParameterJson,
                    this.effect.track.sequencerEngine,
                    this.isCopy);
                this._loadedSignalParameter = true;
            }

            for (let lfo of this.signalParameter.lfos) {
                // since we already loaded the lfos we just
                // need to set up the sub macros wrt parameters
                this.connectToLFO(lfo, false);
            }
        }
    }

    get lfos() {
        return this.signalParameter.lfos;
    }

    free() {
        this.parameters.forEach(
            p => this.removeParameter(p));
    }

    getJson() {
        return {
            name: this.name,
            color: this.color,
            id: this.id,
            number: this.number,
            parameters: this.parameters.map(x => x.id),
            signalParameter: this.signalParameter.getJson()
        };
    }

    removeParameter(parameter) {
        this.lastHull = undefined;
        this.lastCentroid  = undefined;
        if (this._disposed) {
            // already disposed
            return;
        }
        this.parameters = this.parameters.filter(
            x => x !== parameter);
        parameter.planeMacro = undefined;

        // find all sub macros we need to disconect
        let subMacrosToDelete = Object.values(this.subMacros).flat().filter(
            m => m && m.parameter === parameter);

        subMacrosToDelete.forEach(m => this.disposeSubMacro(m));
        
        // remove them from this.subMacros
        for (let lfo in this.subMacros) {
            this.subMacros[lfo] = this.subMacros[lfo].filter(
                m => !subMacrosToDelete.includes(m));
        }
    }

    get isPlaneMacro() {
        return true;
    }

    isEmpty() {
        return this.parameters.length === 0;
    }

    // so that it implements 2DParammable
    getCoordinates() {
        return this.coordinates;
    }

    get coordinates() {
        if (this.parameters.length === 0) {
            return 0;
        }
        let allCoords = this.parameters.map(
            p => p.getCoordinates());
        this._coordinates = centroid(allCoords);
        return this._coordinates;
    }

    get diameter() {
        return diameter(
            this.parameters.map(p => p.getCoordinates()));
    }

    getAllCoordinates(flatArray=false) {
        return getCoordinates(this.parameters, flatArray);
    }

    getGeometry() {
        if (this.parameters.length === 0) {
            return {x:0, y:0, diameter:0};
        }
        let coordinates = this.getAllCoordinates();
        if (!coordinates) {
            return {x:0,y:0,coordinates:undefined};
        }
        return {
            ...getGeometry(coordinates),
            coordinates
        };
   }

    setCoordinates(x1,y1) {
        // find the last coordinates
        let {x, y} = this._coordinates;
        let xDiff = x1-x;
        let yDiff = y1-y;
        this.moveCoordinates(xDiff, yDiff);
    }

    moveCoordinates(...args) {
        let results = this.parameters.map(
            x => x.moveCoordinates(... args));
        // need to update redux
        this.effect.setEffectParameterValues(
            results.reduce((a,{id, value}, {}) => {
                a[id] = value;
                return a;
            }));
    }

    getBlob(x,y, debug, line) {
        let parameters= this.getAllCoordinates();
        let convexHull = makeHull(parameters, {x,y}, 8);
        if (line) {
            convexHull.push(convexHull[0]);
        }
        this.lastHull = convexHull;

        let s = spline(convexHull, window.spline || 1.1, undefined, line);
        return s;
    }

    getEasyBlob() {
        let {x,y, diameter, coordinates} = this.getGeometry();
        let hull;
        if (this.lastHull && dist(x,y,this.lastCentroid.x, this.lastCentroid.y) < 5) {
            hull = this.lastHull.map(x => coordinates[x]); 
        } else if (coordinates) {
            hull = makeHull(coordinates, {x,y}, 8);
            this.lastHull = hull.map(
                ({x,y}) => {
                    let found = coordinates.find(a => a.x === x && a.y === y);
                    return coordinates.indexOf(found);
                });
            this.lastCentroid = {x,y};
        }
        let r = {
            x,
            y,
            diameter,
            coordinates,
            hull,
            d: hull && spline(hull,
                this.effect.splineTension)
        };
        return r;
    }

    getConvexHull(x,y, debug) {
        let coords = this.getAllCoordinates();;
        return makeHull(coords, centroid(coords), debug);
    }

    getTans(x,y) {
        let coords = [];
        for (let parameter of this.parameters) {
            let c = parameter.getCoordinates();
            coords.push(c);
        }
        let convexHull = makeHull(coords);
        let first = convexHull[0];
        if (!first) {
            return "";
        }

        return getTanBlob(convexHull, x,y);
    }
}

const dist = (x1,y1,x2,y2) => {
    return Math.sqrt(Math.pow(x1-x2, 2) + Math.pow(y1-y2, 2));
};
